import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Landmark, Building2, Globe2, Waves, Star, Download, Sparkles } from "lucide-react";
import FilmMark from "@/components/FilmMark";
import ApplicationModal from "@/components/ApplicationModal";
import DownloadGateModal from "@/components/DownloadGateModal";
import { useLang } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const DOSSIER_URL = `${API}/api/mecenat/dossier.pdf`;

// 5 valeurs
const VALUES_I18N = {
  fr: [
    { name: "Héritage", line: "Faire vivre l'esprit des bâtisseurs du canal." },
    { name: "Transmission", line: "Relier les générations." },
    { name: "Création", line: "Donner une place centrale aux artistes." },
    { name: "Territoire", line: "Valoriser les communes traversées." },
    { name: "Ouverture", line: "Créer des ponts entre l'Occitanie, la France, l'Europe, l'Afrique et le reste du monde." },
  ],
  en: [
    { name: "Heritage", line: "Keeping alive the spirit of the canal's builders." },
    { name: "Transmission", line: "Connecting generations." },
    { name: "Creation", line: "Giving artists a central place." },
    { name: "Territory", line: "Showcasing the towns along the way." },
    { name: "Openness", line: "Building bridges between Occitanie, France, Europe, Africa and the rest of the world." },
  ],
};

// Programme 2026 · confirmé — Les Expositions, avec un développé pour l'accordéon
const AXES = [
  {
    n: "01",
    title: "Les Expositions",
    body: "Six escales sur 750 km, de la Montagne Noire à l'Atlantique. Un même fil, six façons de le regarder.",
    detail: [
      "**Canal'Art 2026** réunit six escales le long du canal, de la Montagne Noire à l'Atlantique. Chaque site accueille une exposition pensée pour son paysage, ses pierres, sa lumière — un même fil, six façons de le regarder. Le parcours se déploie d'**octobre à décembre 2026**.",
      "**Six escales, une ligne** — *(Castelnau d'Estrétefonds)* le foyer des Arteurs, chapitre inaugural, ateliers et résidences préparatoires. *(Toulouse)* carte blanche à Olga Yaméogo, peintre du Burkina Faso, sur les berges du canal. *(Saint-Ferréol)* hommage à Michel Batlle, retour au bassin où il sculpta pour les 300 ans de Riquet en 1980. *(Castelnaudary)* le Grand Bassin et le seuil de Naurouze, chapitre du partage des eaux — **Kossi Assou, Trésor vivant de l'UNESCO**. *(Le Malpas)* carte blanche à Arno Brignon, photographe, autour du premier tunnel de navigation au monde. *(Loupian)* étang de Thau et villa gallo-romaine, escale méditerranéenne et clôture du parcours. **Commissariat général** : *Bertrand Ducourau*, conservateur des musées nationaux et directeur de musées.",
      "Après 2026, une exposition itinérante reprend une sélection des œuvres pour circuler dans les communes du Canal, en France et à l'étranger.",
      "**Chaque escale peut être adoptée par un mécène** — voir la page [Mécénat](/mecenat) ou le formulaire [Cercle des 194](#cercle-194).",
    ],
    kpi: ["6 escales · 194 artistes", "octobre → décembre 2026", "itinérance 2027-2028"],
  },
];

// Pourquoi un mécène s'engage
const REASONS_I18N = {
  fr: ["la valorisation d'un patrimoine exceptionnel", "la création artistique", "l'attractivité des territoires", "la transmission culturelle", "la construction d'un récit collectif"],
  en: ["the celebration of an exceptional heritage", "artistic creation", "the attractiveness of the territories", "cultural transmission", "the building of a collective story"],
};

// 5 familles de partenaires (catégories génériques — noms des prospects ciblés
// gardés en interne dans le CRM admin uniquement).
const FAMILIES = [
  {
    icon: Droplets,
    tagline: "Le Canal du Midi est avant tout une histoire d'eau.",
    name: "Les grands partenaires « Eau »",
    body: "Fondations et institutions liées à l'eau, aux fleuves et aux paysages hydrauliques — l'eau comme patrimoine, comme culture et comme lien entre les peuples.",
    tags: ["Fondations d'entreprises", "Agences de bassin", "Grands acteurs de l'eau"],
  },
  {
    icon: Landmark,
    tagline: "Le Canal du Midi est l'un des plus grands patrimoines français.",
    name: "Les grands partenaires « Patrimoine »",
    body: "Fondations reconnues d'utilité publique dédiées à la restauration, à la transmission et à la valorisation du patrimoine bâti et vivant.",
    tags: ["Fondations patrimoniales", "Banques et mutuelles engagées", "Fondations d'entreprises"],
  },
  {
    icon: Building2,
    tagline: "Le cercle à ne surtout pas négliger.",
    name: "Les entreprises occitanes",
    body: "Le tissu économique d'Occitanie — laboratoires, coopératives, presse régionale, énergies renouvelables — pour ancrer le projet dans son territoire d'origine.",
    tags: ["Grands groupes occitans", "Presse & médias régionaux", "Banques mutualistes locales"],
  },
  {
    icon: Globe2,
    tagline: "Une œuvre collective reliant 194 pays autour d'un patrimoine mondial.",
    name: "Les grands mécènes internationaux",
    body: "Fondations européennes et internationales qui portent le dialogue des cultures, la diversité et le patrimoine mondial.",
    tags: ["Fondations européennes", "Fondations américaines", "Institutions multilatérales"],
  },
  {
    icon: Waves,
    tagline: "Peu de projets culturels français osent aller là. Nous avons déjà l'histoire crédible d'AFREEKA.",
    name: "La piste Afrique",
    body: "Fondations d'entreprises françaises présentes en Afrique et fondations africaines — pour tisser un pont artistique Canal du Midi / Afrique.",
    tags: ["Fondations d'entreprises françaises en Afrique", "Fondations africaines", "Réseaux d'artistes africains"],
    accent: true,
  },
];

export default function MecenatPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const VALUES = VALUES_I18N[lang];
  const REASONS = REASONS_I18N[lang];
  const [modal, setModal] = useState({ open: false, kind: "mecenat" });
  const [dl, setDl] = useState({ open: false, doc: "mecenat" });
  return (
    <main className="pt-32 pb-24" data-testid="mecenat-page">
      <FilmMark />
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "Patronage strategy · 2026 → 2030" : "Stratégie Mécénat · 2026 → 2030"}</div>
        <h1 className="mt-6 font-serif font-light text-6xl md:text-8xl leading-[0.9] tracking-tight max-w-5xl">
          {en ? <>Let's weave together <span className="italic">the world's largest open-air</span> thread of art.</> : <>Tissons ensemble <span className="italic">le plus grand fil d'art</span> à ciel ouvert du monde.</>}
        </h1>
        <p className="mt-10 max-w-3xl text-fg/85 leading-relaxed text-xl font-serif italic">
          {en ? '« Le Fil du Midi turns an engineering masterpiece into a permanent collective artwork, where heritage, creation and territories move in the same current. »' : "« Le Fil du Midi transforme un chef-d'œuvre d'ingénierie en œuvre collective permanente, où patrimoine, création et territoires avancent dans le même courant. »"}
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setDl({ open: true, doc: "mecenat" })}
            className="btn-editorial bg-fg text-bg border-fg hover:bg-cinabre hover:border-cinabre"
            data-testid="download-dossier-hero"
          >
            {en ? "Download the PDF pack" : "Télécharger le dossier PDF"} <Download size={14} />
          </button>
          <a href="mailto:contact@lesarteurs.org" className="btn-editorial border-fg/40 text-fg hover:bg-cinabre hover:text-bg hover:border-cinabre">
            {en ? "Write to us" : "Nous écrire"}
          </a>
        </div>
      </section>

      {/* Vision + Réalité */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1300px] mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-start">
          <div>
            <div className="meta-label text-cinabre">§ {en ? "The vision" : "La vision"}</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.98] tracking-tight">
              {en ? <>A cultural work <span className="italic">on the scale of a territory.</span></> : <>Une œuvre culturelle <span className="italic">à l'échelle d'un territoire.</span></>}
            </h2>
            <p className="mt-6 text-fg/80 leading-relaxed text-lg">
              {en ? <>Born of the Canal du Midi — the masterpiece conceived by Pierre-Paul Riquet in the 17th century — the project links heritage, contemporary creation, landscapes, residents and visitors in a living art trail of <span className="text-cinabre">750 kilometres</span>, from the Montagne Noire to the Atlantic.</> : <>Né du Canal du Midi — chef-d'œuvre imaginé par Pierre-Paul Riquet au XVII<sup>e</sup> siècle — le projet relie patrimoine, création contemporaine, paysages, habitants et visiteurs dans un parcours artistique vivant de <span className="text-cinabre">750 kilomètres</span>, de la Montagne Noire à l'Atlantique.</>}
            </p>
            <p className="mt-4 text-fg/80 leading-relaxed text-lg">
              {en ? <>Le Fil du Midi sees the canal not as a frozen monument, but as a <span className="italic">narrative line</span> crossing territories and generations.</> : <>Le Fil du Midi considère le canal non comme un monument figé, mais comme une <span className="italic">ligne narrative</span> traversant les territoires et les générations.</>}
            </p>
          </div>
          <div>
            <div className="meta-label text-cinabre">§ {en ? "Project status" : "L'état du projet"}</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.98] tracking-tight">
              {en ? "Some events have already found their patron." : "Certains événements ont déjà trouvé leur mécène."}
            </h2>
            <p className="mt-6 text-fg/85 leading-relaxed text-lg">
              {en ? "Others are still waiting to be adopted. Every invited artist, every site, every highlight can carry the name of a partner — foundation, company or individual — who chose to accompany it." : "D'autres cherchent encore à se faire adopter. Chaque artiste invité, chaque site, chaque temps fort peut porter le nom d'un partenaire — fondation, entreprise ou particulier — qui aura choisi de l'accompagner."}
            </p>
            <p className="mt-4 text-fg/75 leading-relaxed">
              {en ? <>A rare and intimate way of taking part in a heritage work: seeing your commitment inscribed on the banks of a 17th-century masterpiece, alongside an artist and a country.</> : <>Une manière rare et intime de participer à une œuvre patrimoniale : voir son engagement inscrit dans les rives d'un chef-d'œuvre du XVII<sup>e</sup> siècle, aux côtés d'un artiste et d'un pays.</>}
            </p>

            <Link
              to="/rejoindre?adopt=1"
              className="mt-8 group flex items-start gap-5 border border-cinabre bg-cinabre/5 p-6 hover:bg-cinabre hover:text-bg transition-colors"
              data-testid="adopt-cta"
            >
              <div className="w-11 h-11 border border-cinabre bg-cinabre text-bg flex items-center justify-center shrink-0 group-hover:bg-bg group-hover:text-cinabre transition-colors">
                <Sparkles size={18} strokeWidth={1.4} />
              </div>
              <div className="flex-1">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre group-hover:text-bg/80 transition-colors">§ {en ? "Highlight" : "Encadré"}</div>
                <div className="mt-1 font-serif text-2xl md:text-3xl leading-[1.05] tracking-tight">{en ? "I adopt an artist." : "J'adopte un artiste."}</div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase opacity-70">{en ? "Receive the information sheet" : "Recevoir la fiche de renseignement"}</div>
              </div>
              <ArrowRight size={18} className="mt-2 shrink-0 opacity-70 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* 5 valeurs */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "The 5 values" : "Les 5 valeurs"}</div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="border border-fg/15 p-6 md:p-7 hover:border-cinabre transition-colors"
              data-testid={`value-${i}`}
            >
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">§ 0{i + 1}</div>
              <div className="mt-4 font-serif text-3xl leading-[1] tracking-tight">{v.name}</div>
              <p className="mt-4 text-fg/70 text-sm leading-relaxed">{v.line}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Programme 2026 · confirmé */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "2026 Programme · confirmed" : "Programme 2026 · confirmé"}</div>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.98] tracking-tight max-w-3xl">
          {en ? "A concrete undertaking. One same current." : "Un chantier concret. Un même courant."}
        </h2>
        <p className="mt-4 text-fg/70 max-w-2xl leading-relaxed">
          {en ? "What is funded, dated and committed for 2026. Click to explore in detail. (Detailed content in French.)" : "Ce qui est financé, daté et engagé pour 2026. Cliquez pour découvrir en détail."}
        </p>
        <div className="mt-10 border-t border-fg/15">
          {AXES.map((a, i) => (
            <motion.details
              key={a.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="group border-b border-fg/15 [&_summary::-webkit-details-marker]:hidden"
              data-testid={`axe-${i}`}
            >
              <summary className="cursor-pointer list-none py-8 md:py-10 flex items-start gap-6 md:gap-10 hover:bg-fg/[0.02] transition-colors -mx-6 md:-mx-8 px-6 md:px-8">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre pt-3 shrink-0">§ {a.n}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-3xl md:text-5xl leading-[1] tracking-tight">{a.title}</h3>
                  <p className="mt-3 text-fg/70 leading-relaxed max-w-2xl">{a.body}</p>
                </div>
                <div className="shrink-0 pt-3 md:pt-4">
                  <div className="relative w-8 h-8 md:w-10 md:h-10 border border-fg/25 flex items-center justify-center transition-all group-hover:border-cinabre group-open:border-cinabre group-open:bg-cinabre">
                    <span className="block w-3.5 md:w-4 h-px bg-fg group-open:bg-bg transition-colors" />
                    <span className="absolute block h-3.5 md:h-4 w-px bg-fg group-open:bg-bg group-open:opacity-0 transition-all" />
                  </div>
                </div>
              </summary>
              <div className="pb-10 md:pb-14 pl-0 md:pl-24 pr-4 md:pr-16 grid md:grid-cols-5 gap-8 md:gap-12">
                <div className="md:col-span-3 space-y-5">
                  {a.detail.map((paragraph, pi) => (
                    <p
                      key={pi}
                      className="text-fg/85 leading-[1.75] text-[15px] md:text-base"
                      dangerouslySetInnerHTML={{
                        __html: paragraph
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-fg">$1</strong>')
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline decoration-cinabre decoration-1 underline-offset-4 hover:text-cinabre transition-colors">$1</a>')
                          .replace(/\*(.+?)\*/g, '<em class="italic text-fg/70">$1</em>'),
                      }}
                    />
                  ))}
                </div>
                <div className="md:col-span-2 border-l border-fg/15 pl-6 md:pl-8">
                  <div className="meta-label text-fg/50 mb-4">§ {en ? "2026 indicators" : "Indicateurs 2026"}</div>
                  <ul className="space-y-3">
                    {a.kpi.map((k, ki) => (
                      <li key={ki} className="flex items-baseline gap-3 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-cinabre shrink-0" />
                        <span className="text-fg/80 leading-snug">{k}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.details>
          ))}
        </div>

        {/* Vision pluri-annuelle · 2027-2030 (option b : sobre, projectif, sans over-promise) */}
        <div className="mt-14 md:mt-20 border border-fg/15 p-8 md:p-12 bg-fg/[0.02]" data-testid="vision-2027-2030">
          <div className="meta-label text-fg/50">§ {en ? "Horizon 2027-2030 · vision" : "Horizon 2027-2030 · vision"}</div>
          <h3 className="mt-4 font-serif text-2xl md:text-3xl leading-snug tracking-tight max-w-3xl">
            {en ? "After 2026, the thread keeps weaving." : "Après 2026, le fil continue de se tisser."}
          </h3>
          <p className="mt-5 text-fg/75 leading-relaxed max-w-3xl">
            {en ? <>Beyond the 2026 Exhibitions, we are preparing three multi-year undertakings — <em className="italic">artist residencies</em>, <em className="italic">encounters and mediation</em>, <em className="italic">living documentation of the heritage</em>. These are being co-built with our partners: we will only announce them once funded and dated.</> : <>Au-delà des Expositions de 2026, nous préparons trois chantiers pluri-annuels — <em className="italic">résidences d'artistes</em>, <em className="italic">rencontres et médiation</em>, <em className="italic">documentation vivante du patrimoine</em>. Ces axes sont en cours de co-construction avec nos partenaires : nous ne les annoncerons qu'une fois financés et datés.</>}
          </p>
          <p className="mt-4 text-fg/60 leading-relaxed max-w-3xl text-[15px]">
            {en ? <>Would you like to adopt one of these undertakings, or accompany us over time? <a href="/contact" className="underline decoration-cinabre decoration-1 underline-offset-4 hover:text-cinabre transition-colors">Write to us</a> — we build what comes next with those who commit.</> : <>Vous souhaitez adopter l'un de ces chantiers, ou nous accompagner sur la durée ? <a href="/contact" className="underline decoration-cinabre decoration-1 underline-offset-4 hover:text-cinabre transition-colors">Écrivez-nous</a> — nous construisons la suite avec ceux qui s'engagent.</>}
          </p>
        </div>
      </section>

      {/* Pourquoi un mécène s'engage */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1200px] mx-auto bg-fg text-bg p-10 md:p-16">
          <div className="meta-label text-cinabre">§ {en ? "Why a patron commits" : "Pourquoi un mécène s'engage"}</div>
          <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[0.95] tracking-tight max-w-3xl">
            {en ? <>A patron does not fund an exhibition.<br /> They take part in…</> : <>Le mécène ne finance pas une exposition.<br /> Il participe à…</>}
          </h2>
          <ul className="mt-10 grid md:grid-cols-5 gap-4">
            {REASONS.map((r, i) => (
              <li key={i} className="border border-bg/20 p-5 hover:border-cinabre transition-colors">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">§ 0{i + 1}</div>
                <div className="mt-3 font-serif italic text-lg leading-snug text-bg/90">{r}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5 familles */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "Five families of partners" : "Cinq familles de partenaires"}</div>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.98] tracking-tight max-w-3xl">
          {en ? "One project, five patronage ecosystems." : "Un projet, cinq écosystèmes de mécénat."}
        </h2>

        <div className="mt-14 space-y-6">
          {FAMILIES.map((f, idx) => {
            const Icon = f.icon;
            return (
              <motion.article
                key={f.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className={`border p-8 md:p-12 grid md:grid-cols-12 gap-10 ${f.accent ? "border-cinabre bg-cinabre/5" : "border-fg/15"}`}
                data-testid={`family-${idx}`}
              >
                <div className="md:col-span-4">
                  <div className={`w-12 h-12 border flex items-center justify-center ${f.accent ? "bg-cinabre border-cinabre text-bg" : "border-fg/25 text-fg/70"}`}>
                    <Icon size={20} strokeWidth={1.4} />
                  </div>
                  <div className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">§ {en ? "Family" : "Famille"} 0{idx + 1}</div>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">{f.name}</h3>
                  <p className="mt-4 font-serif italic text-lg text-fg/75 leading-snug">{f.tagline}</p>
                </div>
                <div className="md:col-span-8">
                  <p className="text-fg/80 leading-relaxed text-lg">{f.body}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {f.tags.map((t) => (
                      <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-fg/20 text-fg/70">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Cercle des 194 */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1300px] mx-auto border border-cinabre bg-cinabre/5 p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full border border-cinabre/30 pointer-events-none" />
          <div className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full border border-cinabre/20 pointer-events-none" />

          <div className="relative">
            <div className="meta-label text-cinabre">§ {en ? "The flagship concept" : "Le concept phare"}</div>
            <div className="mt-4 flex items-center gap-4 text-cinabre">
              <Star size={20} strokeWidth={1.4} />
              <div className="font-mono text-[11px] tracking-[0.24em] uppercase">{en ? "A new level for the project" : "Nouveau niveau du projet"}</div>
            </div>
            <h2 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.9] tracking-tight max-w-4xl">
              {en ? <>The Circle of the <span className="text-cinabre italic">194</span>.</> : <>Le Cercle des <span className="text-cinabre italic">194</span>.</>}
            </h2>
            <p className="mt-8 text-xl md:text-2xl font-serif italic text-fg/85 leading-snug max-w-3xl">
              {en ? "194 patrons — companies, foundations, individuals, towns, associations — one per country represented in the project." : "194 mécènes — entreprises, fondations, particuliers, communes, associations — un par pays représenté dans le projet."}
            </p>
            <p className="mt-6 text-fg/80 leading-relaxed max-w-3xl text-lg">
              {en ? <>We no longer merely ask you to fund an exhibition. We invite each patron to <span className="text-cinabre">become the representative of a country</span> in a global artwork born on the banks of the Canal du Midi.</> : <>On ne demande plus seulement de financer une exposition. On propose à chacun de <span className="text-cinabre">devenir le représentant d'un pays</span> dans une œuvre mondiale née sur les rives du Canal du Midi.</>}
            </p>
            <p className="mt-6 text-fg/70 leading-relaxed max-w-3xl">
              {en ? <>This concept lifts Le Fil du Midi out of the "local cultural project" category and into that of <span className="italic">international heritage projects</span>.</> : <>Ce concept fait sortir Le Fil du Midi de la catégorie « projet culturel local » pour entrer dans celle des <span className="italic">projets patrimoniaux internationaux</span>.</>}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setModal({ open: true, kind: "cercle_194" })}
                className="btn-editorial bg-cinabre text-bg border-cinabre hover:bg-fg hover:border-fg"
                data-testid="cercle-cta"
              >
                {en ? "Join the Circle of the 194" : "Rejoindre le Cercle des 194"} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Fiscalité + contact */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1200px] mx-auto bg-fg text-bg p-10 md:p-16 border border-fg">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7">
              <div className="meta-label text-cinabre">§ {en ? "Patronage tax rules" : "Fiscalité du mécénat"}</div>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-[1] tracking-tight">
                {en ? <>Aillagon Law · <span className="text-cinabre">60%</span> tax reduction.</> : <>Loi Aillagon · Réduction fiscale <span className="text-cinabre">60 %</span>.</>}
              </h2>
              <p className="mt-6 text-bg/85 leading-relaxed">
                {en ? "In France, corporate donations to a project of general interest entitle the donor to a corporate tax reduction of 60% of the amount given, up to 0.5% of turnover. Individuals benefit from a 66% income tax reduction." : "En France, les dons des entreprises à un projet d'intérêt général ouvrent droit à une réduction d'impôt sur les sociétés de 60 % du montant versé, dans la limite de 0,5 % du chiffre d'affaires. Les particuliers bénéficient d'une réduction de 66 % de l'IR."}
              </p>
              <p className="mt-4 text-bg/70 text-sm">
                {en ? "Les Arteurs (the carrying structure) issue a tax receipt for every donation. We draw up a tailor-made patronage agreement according to your requirements (visibility, events, artworks, communication)." : "Les Arteurs (structure porteuse) délivrent un reçu fiscal pour chaque don. Nous établissons une convention de mécénat sur mesure selon vos exigences (visibilité, événements, œuvres, communication)."}
              </p>
            </div>
            <div className="md:col-span-5 space-y-4">
              <button
                type="button"
                onClick={() => setDl({ open: true, doc: "mecenat" })}
                className="btn-editorial bg-cinabre text-bg border-cinabre hover:bg-bg hover:text-fg hover:border-bg w-full justify-center"
                data-testid="download-dossier-fiscal"
              >
                {en ? "Download the PDF pack" : "Télécharger le dossier PDF"} <Download size={14} />
              </button>
              <Link to="/contact" className="btn-editorial bg-bg text-fg border-bg hover:bg-cinabre hover:text-bg hover:border-cinabre w-full justify-center" data-testid="mecenat-contact-cta">
                {en ? "Get in touch" : "Prendre contact"} <ArrowRight size={14} />
              </Link>
              <a href="mailto:contact@lesarteurs.org" className="btn-editorial border-bg/40 text-bg hover:bg-cinabre hover:border-cinabre w-full justify-center">
                contact@lesarteurs.org
              </a>
              <div className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-bg/60">
                {en ? "Full pack · 4 pages · immediate download." : "Dossier complet · 4 pages · téléchargement immédiat."}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ApplicationModal
        open={modal.open}
        onClose={() => setModal({ ...modal, open: false })}
        kind={modal.kind}
      />
      <DownloadGateModal
        open={dl.open}
        onClose={() => setDl({ ...dl, open: false })}
        document={dl.doc}
      />
    </main>
  );
}
